import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { walkiesClient } from "../api/walkiesClient";
import type { WalkerSessionDto } from "../types/auth";

type AuthState = "idle" | "loading" | "ready";

type WalkerAuthContextValue = {
  state: AuthState;
  me: WalkerSessionDto | null;
  isAuthenticated: boolean;
  refreshMe: () => Promise<void>;
  signOut: () => Promise<void>;
};

const WalkerAuthContext = createContext<WalkerAuthContextValue | undefined>(undefined);

export const WalkerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>("idle");
  const [me, setMe] = useState<WalkerSessionDto | null>(null);

  async function refreshMe() {
    try {
      setState("loading");
      const data = await walkiesClient.getWalkerMe();
      setMe(data ?? null);
    } catch {
      setMe(null);
    } finally {
      setState("ready");
    }
  }

  async function signOut() {
    try {
      await walkiesClient.signOutWalker();
    } catch {
      // ignore sign-out API failure and clear client auth state anyway
    } finally {
      setMe(null);
      setState("ready");
    }
  }

  useEffect(() => {
    void refreshMe();
  }, []);

  const value = useMemo<WalkerAuthContextValue>(
    () => ({
      state,
      me,
      isAuthenticated: !!me,
      refreshMe,
      signOut,
    }),
    [state, me]
  );

  return (
    <WalkerAuthContext.Provider value={value}>
      {children}
    </WalkerAuthContext.Provider>
  );
};

export function useWalkerAuth(): WalkerAuthContextValue {
  const ctx = useContext(WalkerAuthContext);

  if (!ctx) {
    throw new Error("useWalkerAuth must be used within a WalkerAuthProvider.");
  }

  return ctx;
}